import { apiFetch } from '@/lib/apiClient';
import type { NotificationLabelColor } from '@coreltb/shared/constants';
import type { Notification } from '@coreltb/shared/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// 'unread' = legacy alias dla 'todo' (migration 0043). Nowy frontend uzywa 'todo'.
export type NotificationFilter = 'todo' | 'all' | 'done' | 'unread';

const notificationKeys = {
  all: ['notifications'] as const,
  // limit w kluczu: dzwonek (20) i strona (200) nie mogą nadpisywać sobie listy.
  list: (filter: NotificationFilter, limit: number) =>
    [...notificationKeys.all, 'list', filter, limit] as const,
  prefs: () => [...notificationKeys.all, 'prefs'] as const,
};

interface NotificationsListData {
  items: Notification[];
  unreadCount: number;
}

/**
 * Lista powiadomien usera. Polling 10s (jak reszta aplikacji).
 *
 * filter='unread' — tylko nieprzeczytane (default, dla AttentionBell)
 * filter='all' — wszystkie (dla strony /notyfikacje)
 *
 * Backend zwraca { data: { items, unreadCount } } — apiFetch wyciaga `data`
 * (caly obiekt), wiec mamy items + unreadCount w jednym query.
 */
export function useNotifications(filter: NotificationFilter = 'todo', limit = 50, enabled = true) {
  return useQuery({
    queryKey: notificationKeys.list(filter, limit),
    enabled,
    queryFn: () =>
      apiFetch<NotificationsListData>('/api/notifications', {
        query: { filter, limit },
      }),
    staleTime: 30 * 1000,
    // Auto-refresh co 30s zeby dzwoneczek pokazywal swieze powiadomienia
    // bez konieczności F5. Nie blokuje UX bo to background fetch.
    refetchInterval: 30 * 1000,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ id: string; readAt: number; success: boolean }>(`/api/notifications/${id}/read`, {
        method: 'PATCH',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationKeys.all }),
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (opts?: { resolved?: boolean }) =>
      apiFetch<{ readAt: number; resolved: boolean }>('/api/notifications/mark-all-read', {
        method: 'POST',
        body: { resolved: opts?.resolved ?? false },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationKeys.all }),
  });
}

/**
 * PATCH /api/notifications/:id/resolve — manual resolve (button "Załatwione" w UI).
 * TODO -> RESOLVED, znika z bell counter + filtru "Do akcji".
 */
export function useResolveNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ id: string; resolvedAt: number }>(`/api/notifications/${id}/resolve`, {
        method: 'PATCH',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationKeys.all }),
  });
}

/**
 * Kilka powiadomień naraz (karta klienta): `read` — zostają w liczniku jako „do zrobienia”,
 * `resolve` — załatwione. Jedno odświeżenie listy na końcu.
 */
export function useNotificationBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ read = [], resolve = [] }: { read?: string[]; resolve?: string[] }) =>
      Promise.all([
        ...read.map((id) => apiFetch(`/api/notifications/${id}/read`, { method: 'PATCH' })),
        ...resolve.map((id) => apiFetch(`/api/notifications/${id}/resolve`, { method: 'PATCH' })),
      ]),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationKeys.all }),
  });
}

/** „Odłóż” przypomnienie: nowy termin w leadzie, zamyka tylko to jedno powiadomienie. */
export function useSnoozeNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, until }: { id: string; until: number }) =>
      apiFetch(`/api/notifications/${id}/snooze`, { method: 'POST', body: { until } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationKeys.all }),
  });
}

// ===== Notification preferences (per-user) =====

interface NotificationPrefsData {
  role: string;
  prefs: Record<string, boolean> | null;
}

/** GET /api/users/me/notification-prefs */
export function useNotificationPrefs() {
  return useQuery({
    queryKey: notificationKeys.prefs(),
    queryFn: () => apiFetch<NotificationPrefsData>('/api/users/me/notification-prefs'),
    staleTime: 5 * 60 * 1000, // 5 min — prefs rzadko sie zmieniaja
  });
}

/** PATCH /api/users/me/notification-prefs — pelna mapa Record<NotificationType, boolean>. */
export function useUpdateNotificationPrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (prefs: Record<string, boolean>) =>
      apiFetch<{ prefs: Record<string, boolean> }>('/api/users/me/notification-prefs', {
        method: 'PATCH',
        body: prefs,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationKeys.prefs() }),
  });
}

// ===== Etykiety kart klienta (mig 0082) — prywatne per użytkownik =====

export interface NotificationLabelsData {
  names: Record<NotificationLabelColor, string>;
  leads: Record<string, NotificationLabelColor>;
}

const labelsKey = ['notification-labels'] as const;

export function useNotificationLabels() {
  return useQuery({
    queryKey: labelsKey,
    queryFn: () => apiFetch<NotificationLabelsData>('/api/notification-labels'),
    staleTime: 5 * 60 * 1000,
  });
}

/** Przypnij / zdejmij etykietę klienta — od razu widać zmianę (optimistic). */
export function useSetLeadLabel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ leadId, color }: { leadId: string; color: NotificationLabelColor | null }) =>
      apiFetch(`/api/notification-labels/leads/${leadId}`, { method: 'PUT', body: { color } }),
    onMutate: async ({ leadId, color }) => {
      await qc.cancelQueries({ queryKey: labelsKey });
      const prev = qc.getQueryData<NotificationLabelsData>(labelsKey);
      if (prev) {
        const leads = { ...prev.leads };
        if (color) leads[leadId] = color;
        else delete leads[leadId];
        qc.setQueryData<NotificationLabelsData>(labelsKey, { ...prev, leads });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(labelsKey, ctx.prev);
    },
    // Serwer jest źródłem prawdy — po zmianie dociągamy etykiety jeszcze raz.
    onSettled: () => qc.invalidateQueries({ queryKey: labelsKey }),
  });
}

export function useSetLabelNames() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (names: Partial<Record<NotificationLabelColor, string>>) =>
      apiFetch('/api/notification-labels/names', { method: 'PUT', body: { names } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: labelsKey }),
  });
}
